import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { TableButtonsProps } from "../models/TableButton.model";
import { RouteParams } from "../models/VoyagePnl.model";
import { continueClickedAtomFamily, enabledFlagsAtomFamily, submitEnabledAtomFamily } from "../utils/UtilFunctions";
import ApproveRejectCommentBox from "./ApproveRejectCommentBox";
import CancelConfirmation from "./CancelConfirmationMessage";
import { ReusableButton } from "./ReusableTableButton";
import { useAtom, useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const TableButtons: React.FC<TableButtonsProps> = ({ onReset }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pnlApproved, setPnlApproved] = useState(false);
  const { hasFeature } = useFeatureAccess();

  const { tcCode, voyageNo, isApprover } = useParams<RouteParams>();
  const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);
  const [continueClicked, setContinueClicked] = useAtom(continueClickedAtomFamily(voyageScope));
  const [, setSubmitEnabled] = useAtom(submitEnabledAtomFamily(voyageScope));
  const enabledDrivers = useAtomValue(enabledFlagsAtomFamily(voyageScope)) > 0;

  const userCanReviewPnl = isApprover === "approval" && hasFeature(FEATURE_KEYS.REVIEW_PNL);

  const handleCancelExit = () => {
    if (enabledDrivers) {
      setShowConfirmation(true);
    }
  };

  const handleGoBack = () => {
    setContinueClicked(false);
    setSubmitEnabled(false);
  };

  const handleContinue = () => {
    if (!enabledDrivers) return;
    setContinueClicked(true);
  };

  const handleSubmit = () => {
    if (!enabledDrivers) return;
    setSubmitEnabled(true);
  };

  const handleConfirmYes = () => {
    setShowConfirmation(false);
    onReset();
  };

  const handleConfirmNo = () => {
    setShowConfirmation(false);
  };

  const handleApproveClick = () => {
    setPnlApproved(true);
    setShowCommentModal(true);
  };

  const handleRejectClick = () => {
    setPnlApproved(false);
    setShowCommentModal(true);
  };

  const handleCloseCommentModal = () => {
    setPnlApproved(false);
    setShowCommentModal(false);
  };

  return (
    <>
      <div className="mb-4 flex justify-end gap-4">
        {userCanReviewPnl ? (
          <>
            <ReusableButton width={120} buttonText="Decline" onClickHandler={handleRejectClick} />
            <ReusableButton width={120} buttonText="Approve" onClickHandler={handleApproveClick} />
          </>
        ) : (
          <>
            {!continueClicked ? (
              <>
                <ReusableButton width={140} buttonText="Cancel and exit" onClickHandler={handleCancelExit} />
                <ReusableButton width={100} buttonType="button" buttonText="Continue" onClickHandler={handleContinue} />
              </>
            ) : (
              <>
                <ReusableButton width={140} buttonText="Go back" onClickHandler={handleGoBack} />
                <ReusableButton width={100} buttonType="submit" buttonText="Submit" onClickHandler={handleSubmit} />
              </>
            )}
          </>
        )}
      </div>

      {showConfirmation && (
        <CancelConfirmation
          message="Are you sure you want to cancel and exit?"
          onClickYes={handleConfirmYes}
          onClickNo={handleConfirmNo}
        />
      )}

      {showCommentModal && <ApproveRejectCommentBox onCancel={handleCloseCommentModal} approved={pnlApproved} />}
    </>
  );
};

export default TableButtons;
